import React from "react";
import { TextField, Button } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { AppDispatch } from "@/store";
import {
  setTempEmail,
  setLoading,
  setForgotPasswordToken,
} from "@/store/slices/authSlice";
import { useTranslation } from "react-i18next";

const { unAuthenticatedCall: forgotPasswordMethod } =
  actionsHandler("forgotPassword");

const ForgetPassword = () => {
  const { t } = useTranslation("auth");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    emailOrPhone: Yup.string()
      .required(t("forgetPassword.form.emailOrPhone.validation.required"))
      .test(
        "email-or-phone",
        t("forgetPassword.form.emailOrPhone.validation.invalid"),
        (value) => {
          // Email regex
          const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
          // Phone regex (assumes 10 digit number)
          const phoneRegex = /^[0-9]{10}$/;

          return emailRegex.test(value) || phoneRegex.test(value);
        },
      ),
  });

  const formik = useFormik({
    initialValues: {
      emailOrPhone: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(setLoading(true));
        const response = await dispatch(
          forgotPasswordMethod({
            payload: {
              email: values.emailOrPhone,
            },
            method: "post",
          }),
        );

        if (response.ok) {
          dispatch(setTempEmail(values.emailOrPhone));
          dispatch(setForgotPasswordToken(response.data.token));
          navigate("/email-verification");
          toast.success(
            response.data.message || t("forgetPassword.messages.otpSent"),
          );
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error(t("forgetPassword.messages.unexpectedError"));
      } finally {
        dispatch(setLoading(false));
      }
    },
  });

  return (
    <div className="tw-min-h-screen tw-flex">
      {/* Left Section: Background Image */}
      <div className="lg:tw-w-3/4 md:tw-w-2/4 sm:tw-w-full tw-hidden login-bg tw-bg-cover tw-bg-center sm:tw-flex tw-items-end tw-justify-start tw-text-white tw-p-14">
        <div className="tw-px-4">
          <h1 className="tw-font-bold font-size-5xl tw-mb-4 tw-leading-[74px]">
            Innovative Collateral & <br /> Risk Solutions
          </h1>
        </div>
      </div>

      {/* Right Section: Form */}
      <div className="lg:tw-w-1/3 md:tw-w-2/4 sm:tw-w-full tw-w-full tw-flex tw-items-center tw-justify-center">
        <div className="tw-w-full tw-max-w-md tw-px-16">
          <div className="tw-mb-8">
            <img
              src="/logo/Logo.svg"
              alt="Logo"
              className="tw-mx-auto tw-max-w-[150px] tw-mb-14"
            />
            <h5 className="tw-text-[22px] font-primary font-primary-color font-weight-semibold tw-leading-6">
              {t("forgetPassword.title")}
            </h5>
            <p className="tw-mt-2 tw-leading-6 font-size-sm font-gray-color font-weight-medium">
              {t("forgetPassword.subtitle")}
            </p>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="form-group tw-mb-6">
              <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                {t("forgetPassword.form.emailOrPhone.label")}
              </p>
              <TextField
                fullWidth
                name="emailOrPhone"
                type="text"
                variant="outlined"
                placeholder={t("forgetPassword.form.emailOrPhone.placeholder")}
                value={formik.values.emailOrPhone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.emailOrPhone &&
                  Boolean(formik.errors.emailOrPhone)
                }
                helperText={
                  formik.touched.emailOrPhone && formik.errors.emailOrPhone
                }
                className="tw-mb-4"
              />
            </div>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="tw-py-2 primary-custom-button"
            >
              {t("forgetPassword.form.submit")}
            </Button>

            <div className="tw-text-center tw-mt-4">
              <a href="/" className="tw-text-sm tw-font-medium font-gray-color">
                {t("signIn.backToLogin")}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
